import { Client } from "@xmpp/client"
import * as Sentry from "@sentry/react"
import { pingServer } from "../api/xmppPing"
import XmppApi from "../api/xmppApi"
import XmppPubSubApi from "../api/xmppPubSubApi"
import { retry } from "../utils"
import { captureError } from "../ErrorHandlers"
import XmppVCardApi from "../api/xmppVCardApi"
import { upsertProfile } from "../reducers/profilesSlice"
import { USER_CHAT_NS } from "@/lib/accountChatsParser"
import { retrieveAllChatsSettings } from "@/api/xeps/ChatsSettings"
import { batchAddChatsSettings } from "@/reducers/chatsSettingsSlice"

let pingEvery: number = 1
let consecutiveFailedPings: number = 0
let scheduleSync: boolean = true

const MAX_FAILED_PINGS_TO_CONSIDER_FAULTY = 3

const setPingEvery = (value: number) => {
  pingEvery = value
}

const getPingInterval = (delay: number | null) => {
  if (delay) {
    if (pingEvery < 32) setPingEvery(pingEvery * 2)
  } else {
    setPingEvery(1)
  }

  return pingEvery
}

const trackEvent = (message: string) => {
  window.analytics.track("KeepConnected", {
    pingEvery,
    consecutiveFailedPings,
    scheduleSync,
    message,
  })
}

const getIsHealthyConnection = (): boolean => {
  return consecutiveFailedPings < MAX_FAILED_PINGS_TO_CONSIDER_FAULTY
}

const subscribeToOwnBookmarks = async (client: Client) => {
  const jid = client.jid?.bare().toString() as string
  try {
    await XmppPubSubApi.subscribe(client, jid, "urn:xmpp:bookmarks:1")
  } catch (error: any) {
    if (error.condition !== "item-not-found") {
      captureError(error, {
        origin: "XMPPOnOnline",
        extra: { message: "subscribeToOwnBookmarks" },
      })
    }
  }
}

export const syncSelfVcard = async (client: Client) => {
  if (!client.jid) return

  const jid = client.jid.bare().toString()

  try {
    const vcard = await XmppVCardApi.fetchVCard(window.XMPP!, jid)
    if (!vcard) return

    window.store.dispatch(upsertProfile({ profile: vcard }))
  } catch (error: any) {
    console.warn(`Cannot get self vcard for ${jid}`)
  }
}

const fetchAllChatsSettings = async (client: Client) => {
  const settings = await retrieveAllChatsSettings(client)
  window.store.dispatch(batchAddChatsSettings({ settings }))
}

const runFullSync = async (client: Client) => {
  trackEvent("Full sync")
  if (!(await pingServer(client, 1000))) {
    try {
      await client.stop()
    } catch (error) {
      console.warn("Cannot stop connection, continuing anyway", error)
      // pass
    }
    await client.start()
  }
  await Sentry.startSpan({ name: "KeepConnected" }, async () => {
    await fetchAllChatsSettings(client)
    await subscribeToOwnBookmarks(client)
    await syncSelfVcard(client)
    await XmppApi.sendPresence(client)
    console.warn("KeepConnected connection restarted")
  })
}

const keepConnectedIteration = async (client: Client): Promise<number> => {
  if (document.visibilityState === "hidden") {
    return 2000
  }

  const delay = await pingServer(client, 5000)
  if (delay) {
    consecutiveFailedPings = 0
  } else {
    trackEvent("Ping Failed")
    consecutiveFailedPings += 1
  }
  const isHealthy = getIsHealthyConnection()
  if (!delay) {
    console.warn("KeepConnected ping failed", {
      pingEvery,
      isHealthy,
      consecutiveFailedPings,
      scheduleSync,
    })
  }

  if (!isHealthy) {
    scheduleSync = true
  }

  if (scheduleSync) {
    try {
      console.warn("KeepConnected sync", { scheduleSync, isHealthy })
      await retry(runFullSync, [client], {
        maxAttempts: 1000,
        backoff: true,
      })
      scheduleSync = false
      setPingEvery(1)
    } catch {
      console.warn("KeepConnected could not do full sync")
    }
  }

  const nextInterval = getPingInterval(delay)
  return 1000 * nextInterval
}

const keepConnectedLoop = async (client: Client) => {
  let timeout = 2000
  try {
    timeout = await keepConnectedIteration(client)
  } catch (error: any) {
    console.error("Error during keepConnected")
  } finally {
    console.log("KeepConnected loop", timeout)
    setTimeout(() => {
      void keepConnectedLoop(client)
    }, timeout)
  }
}

let currentVisibilityState: null | "visible" | "hidden" = null

window.addEventListener("visibilitychange", () => {
  if (currentVisibilityState !== document.visibilityState) {
    console.log(
      `KeepConnected page visibility change ${currentVisibilityState} -> ${document.visibilityState}`,
    )
    setPingEvery(1)
    currentVisibilityState = document.visibilityState
  }
})

const disconnectOnLeavingPage = async (xmpp: Client) => {
  window.analytics.track("PageUnload")
  console.info("Disconnecting XMPP before quitting")
  await xmpp.stop()
}

export const keepConnected = (xmpp: Client) => {
  console.log("KeepConnected start")
  xmpp.on("online", () => {
    scheduleSync = true
  })
  xmpp.reconnect.on("reconnected", () => {
    scheduleSync = true
  })

  window.addEventListener("beforeunload", async () => {
    await disconnectOnLeavingPage(xmpp)
  })
  void keepConnectedLoop(xmpp)
}
